import { SOCIAL_OG_IMAGE, SOCIAL_OG_IMAGE_ALT } from "@/lib/social-image";
import { BlogHero } from "@/components/BlogHero";
import { blogHeroUrl, blogHeroAlt } from "@/data/_media/blog-hero";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ctrDesc, ctrTitle } from "@/lib/seo-copy";
import { SITE } from "@/lib/site-config";
import {
  buildJsonLd,
  jsonLdScript,
  webPageSchema,
  breadcrumbListSchema,
  articleSchema,
  faqPageSchema,
} from "@/lib/schema";
import { canonicalAndHreflang } from "@/lib/hreflang";

/** Korean mirror of the apostille vs legalisation guide. */
const TITLE = "아포스티유와 영사확인의 차이 — 태국 서류 국가별 가이드";
const DESC =
  "태국 서류를 해외에서 사용하기 위한 인증 방법: 아포스티유와 영사확인의 차이, 올바른 순서, 주요 국가별 유의점을 정리했습니다.";
const PATH = "/blog/apostille-vs-legalization-by-country";
const URL = `/ko${PATH}`;
const PUBLISHED = "2026-08-10";

const FAQ = [
  {
    question: "아포스티유란 무엇입니까?",
    answer:
      "헤이그 협약에 따른 한 장의 증명서로, 협약 가입국 사이에서는 영사확인 절차를 대체합니다. 제출 기관이 가입국이고 이를 수리하는 경우에만 대사관·영사관 확인을 생략할 수 있습니다.",
  },
  {
    question: "영사확인은 어떤 순서로 진행됩니까?",
    answer:
      "원본 또는 발급기관의 인증된 사본을 확보하고, 사문서는 공증(Notary) 절차를 거친 뒤 외교부 영사국 확인, 번역, 마지막으로 제출 대상국 대사관·영사관 확인 순서로 진행합니다.",
  },
  {
    question: "번역과 인증 중 무엇이 먼저입니까?",
    answer:
      "원칙적으로 인증을 받은 후 번역합니다. 먼저 번역하면 이후에 추가되는 인증 도장과 문구가 번역문에 반영되지 않아 반송 사유가 됩니다.",
  },
  {
    question: "사본으로도 인증이 가능합니까?",
    answer:
      "대부분 불가합니다. 원본 또는 발급기관이 발행한 인증 사본이 필요하며, 단순 복사본은 일반적으로 수리되지 않습니다.",
  },
  {
    question: "서류에 유효기간이 있습니까?",
    answer:
      "가족관계 서류나 범죄경력증명서 등은 발급 후 1~6개월 이내를 요구하는 기관이 많습니다. 각 단계마다 영업일이 소요되므로 제출 기한에서 거꾸로 계산해 계획하십시오.",
  },
  {
    question: "위임장으로 대리 신청할 수 있습니까?",
    answer:
      "가능하지만 신청·수령·인증 절차·제출까지 모두 위임 범위에 포함되어야 합니다. 범위가 좁으면 중간에 절차가 멈춥니다.",
  },
  {
    question: "반송되는 주된 원인은 무엇입니까?",
    answer:
      "순서 역전, 도장·스탬프 문구 번역 누락, 이름 표기 불일치, 유효기간 초과, 단순 사본 제출 등입니다.",
  },
];

export const Route = createFileRoute("/ko/blog/apostille-vs-legalization-by-country")({
  component: KoApostilleVsLegalisation,
  loader: () => ({
    h: {
      meta: [
        { title: ctrTitle(`${TITLE} — ${SITE.name}`) },
        { name: "description", content: ctrDesc(DESC) },
        { property: "og:title", content: ctrTitle(TITLE) },
        { property: "og:description", content: ctrDesc(DESC) },
        { property: "og:type", content: "article" },
        { property: "og:url", content: `https://${SITE.canonicalHost}${URL}` },
        { property: "og:locale", content: "ko" },
        { name: "twitter:card", content: "summary_large_image" },
        { property: "og:image", content: blogHeroUrl("apostille-vs-legalization-by-country") ?? SOCIAL_OG_IMAGE },
        { property: "og:image:alt", content: blogHeroAlt("apostille-vs-legalization-by-country", "ko") ?? SOCIAL_OG_IMAGE_ALT },
        { name: "twitter:image", content: blogHeroUrl("apostille-vs-legalization-by-country") ?? SOCIAL_OG_IMAGE },
        {
          name: "keywords",
          content:
            "태국 아포스티유, 태국 영사확인, 태국 서류 인증 절차, 태국 외교부 영사국, 태국 공증",
        },
      ],
      links: canonicalAndHreflang(PATH, null, "ko"),
      scripts: [
        jsonLdScript(
          buildJsonLd([
            webPageSchema({ url: URL, title: TITLE, description: DESC, lastModified: PUBLISHED, speakable: true }),
            breadcrumbListSchema([
              { name: "홈", url: "/ko" },
              { name: "가이드", url: "/blog" },
              { name: TITLE, url: URL },
            ]),
            articleSchema({
            image: blogHeroUrl("apostille-vs-legalization-by-country"),
              url: URL,
              headline: TITLE,
              description: DESC,
              datePublished: PUBLISHED,
              dateModified: PUBLISHED,
              articleSection: "Apostille & Legalisation",
            }),
            faqPageSchema(FAQ),
          ]),
        ),
      ],
    },
  }),
  head: ({ loaderData }) => loaderData?.h ?? {},
});

const SEQUENCE: string[] = [
  "제출 기관에 필요한 인증 단계·수리 언어·유효기간을 먼저 확인한다",
  "원본 또는 발급기관의 인증 사본을 확보한다",
  "사문서는 공증인의 서명 인증을 받는다",
  "필요에 따라 외교부 영사국 확인을 받는다",
  "도장·스탬프·주석까지 포함해 전문을 번역한다",
  "제출 대상국 대사관·영사관에서 영사확인을 받거나, 가능하면 아포스티유 경로로 전환한다",
];

const COUNTRIES: Array<{ c: string; note: string }> = [
  { c: "호주", note: "다수 기관이 NAATI 인증 번역을 요구하며, 서류에 따라 영사국 확인도 필요합니다." },
  { c: "미국", note: "제출 주(州)와 기관에 따라 다르며, 공증 외 추가 인증을 요구할 수 있습니다." },
  { c: "중국 본토", note: "완전한 인증 연쇄를 요구하는 것이 일반적이며, 번역문이 원본과 정확히 대응해야 합니다." },
  { c: "한국·일본", note: "영사국 확인 후 대사관·영사관 확인이 일반적입니다. 이름 표기는 여권과 일치시켜야 합니다." },
  { c: "유럽 각국", note: "제출 기관이 협약 가입국으로서 아포스티유를 수리하면 대사관 확인을 생략할 수 있습니다." },
  { c: "중동 각국", note: "완전한 영사확인 연쇄와 함께 번역 언어를 지정하는 경우가 많습니다." },
];

const MISTAKES: string[] = [
  "인증보다 번역을 먼저 진행해 인증 내용이 번역문에 없다",
  "원본·인증 사본이 아닌 단순 복사본을 사용했다",
  "같은 서류철 안에서 이름 표기가 일치하지 않는다",
  "너무 이른 발급으로 제출 시점에 유효기간이 지났다",
  "위임 범위가 좁아 후속 인증·제출이 포함되지 않았다",
  "도장·스탬프·주석 문구를 번역에서 빠뜨렸다",
];

function KoApostilleVsLegalisation() {
  return (
    <article className="mx-auto max-w-3xl px-4 py-12 prose prose-slate dark:prose-invert">
      <nav className="text-sm mb-6 not-prose">
        <Link to="/ko" className="text-muted-foreground hover:text-foreground">
          홈
        </Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <Link to="/blog" className="text-muted-foreground hover:text-foreground">
          가이드
        </Link>
      </nav>

      <h1>{TITLE}</h1>
      <BlogHero slug="apostille-vs-legalization-by-country" locale="ko" />
      <p className="answer-summary speakable-summary">
        태국 서류가 반송되는 원인은 내용 오류보다 인증 순서 착오, 도장 문구 번역 누락, 유효기간 초과입니다.
        제출 기한에서 거꾸로 계산하고 제출 기관 요건을 확인한 뒤 “원본 → 공증 → 영사국 → 번역 → 대사관” 순서로 진행하는 것이 안전합니다.
      </p>

      <h2>1. 두 경로의 차이</h2>
      <p>
        아포스티유는 한 장의 증명서로 인증 연쇄를 대체하며 협약 가입국 사이에서 상호 인정됩니다.
        영사확인은 각 단계를 순서대로 밟고 마지막에 제출 대상국 재외공관이 확인합니다. 판단 기준은 “제출 기관이 현재 어느 쪽을 수리하는가” 한 가지이며, 반드시 최신 안내로 확인하십시오.
      </p>

      <h2>2. 올바른 순서</h2>
      <ol>
        {SEQUENCE.map((s) => (
          <li key={s}>{s}</li>
        ))}
      </ol>

      <h2>3. 국가별 유의점</h2>
      <ul>
        {COUNTRIES.map((x) => (
          <li key={x.c}>
            <strong>{x.c}:</strong> {x.note}
          </li>
        ))}
      </ul>

      <h2>4. 자주 나오는 반송 사유</h2>
      <ul>
        {MISTAKES.map((m) => (
          <li key={m}>{m}</li>
        ))}
      </ul>

      <h2>5. 자주 묻는 질문</h2>
      <dl>
        {FAQ.map((f) => (
          <div key={f.question}>
            <dt>
              <strong>{f.question}</strong>
            </dt>
            <dd>{f.answer}</dd>
          </div>
        ))}
      </dl>

      <h2>6. 참고 링크 및 관련 서비스</h2>
      <p className="text-sm">
        참고:{" "}
        <a href="https://consular.mfa.go.th/en/publicservice/legalization" target="_blank" rel="noreferrer">
          태국 외교부 영사국
        </a>{" "}
        ·{" "}
        <a href="https://www.hcch.net/en/instruments/conventions/specialised-sections/apostille" target="_blank" rel="noreferrer">
          HCCH 아포스티유 협약
        </a>
      </p>
      <ul className="not-prose mt-4 grid gap-2 text-sm">
        <li>
          <Link to="/ko/lgz" className="text-primary underline">
            서류 인증·영사확인 서비스
          </Link>
        </li>
        <li>
          <Link to="/ko/notary" className="text-primary underline">
            공증(Notary) 서비스
          </Link>
        </li>
        <li>
          <Link to="/ko/naati" className="text-primary underline">
            NAATI 인증 번역
          </Link>
        </li>
      </ul>
      <p className="text-sm text-muted-foreground">
        내용 확인일 {PUBLISHED}. 본 가이드는 준비를 위한 일반 정보이며 어떤 기관의 결정을 보장하지 않습니다. 진행 전 발급기관과 제출 기관에서 최신 요건을 확인하십시오.
      </p>
    </article>
  );
}
